import { useState, useEffect, useRef, useCallback } from 'react';
import { MAPS_KEY, MAP_ID } from '../maps-config';
import { properties } from '../data/properties';
import { useT } from '../i18n/useT';
import { properties as propEn } from '../i18n/translations/en';
import { properties as propEs } from '../i18n/translations/es';
import './Properties.css';

const VENUS_CENTER = { lat: 32.438, lng: -97.099 };

/* ── Photo with fallback extension ─────────────────────────── */
const PropPhoto = ({ photoKey, description, active, onClick }) => {
    const [src, setSrc] = useState(`/assets/images/properties/${photoKey}.jpg`);
    const [failed, setFailed] = useState(false);

    const handleError = () => {
        if (src.endsWith('.jpg')) {
            setSrc(`/assets/images/properties/${photoKey}.JPG`);
        } else {
            setFailed(true);
        }
    };

    if (failed) return null;
    return (
        <img
            src={src}
            alt={`${description} ${photoKey}`}
            className={`prop-modal-photo${active ? ' active' : ''}`}
            onError={handleError}
            onClick={onClick}
        />
    );
};

/* ── Property Modal ─────────────────────────────────────────── */
const PropertyModal = ({ property, onClose }) => {
    const [activePhoto, setActivePhoto] = useState(null);

    useEffect(() => {
        setActivePhoto(null);
        const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [property, onClose]);

    if (!property) return null;

    const typeLabel = property.type === 'sale' ? 'For Sale'
        : property.type === 'lease' ? 'For Lease'
        : 'Sale / Lease';

    return (
        <div className="prop-modal-overlay" onClick={onClose}>
            <div className="prop-modal" onClick={e => e.stopPropagation()}>
                <button className="prop-modal-close" onClick={onClose}>✕</button>

                {/* Photos */}
                <div className="prop-modal-gallery">
                    {(property.photos || []).map(key => (
                        <PropPhoto
                            key={key}
                            photoKey={key}
                            description={property.description}
                            active={activePhoto === key}
                            onClick={() => setActivePhoto(key === activePhoto ? null : key)}
                        />
                    ))}
                    {(property.photos || []).length === 0 && (
                        <div className="prop-no-photo">🏗 No photos yet</div>
                    )}
                </div>

                {/* Details */}
                <div className="prop-modal-body">
                    <div className="prop-modal-header">
                        <span className={`prop-badge prop-badge--${property.type}`}>{typeLabel}</span>
                        <h2 className="prop-modal-title">{property.description}</h2>
                        <p className="prop-modal-address">
                            {property.address} · {property.city}, {property.state} {property.zipCode}
                        </p>
                    </div>

                    <div className="prop-modal-stats">
                        {property.saleCost && (
                            <div className="prop-stat">
                                <span className="prop-stat-label">Sale Price</span>
                                <strong className="prop-stat-value prop-stat-value--price">{property.saleCost}</strong>
                            </div>
                        )}
                        {property.leaseCost && (
                            <div className="prop-stat">
                                <span className="prop-stat-label">Lease Cost</span>
                                <strong className="prop-stat-value prop-stat-value--price">{property.leaseCost}</strong>
                            </div>
                        )}
                        {property.acreage && (
                            <div className="prop-stat">
                                <span className="prop-stat-label">Acreage</span>
                                <strong className="prop-stat-value">{property.acreage} ac</strong>
                            </div>
                        )}
                        {property.landSqft && (
                            <div className="prop-stat">
                                <span className="prop-stat-label">Land Area</span>
                                <strong className="prop-stat-value">{Number(property.landSqft).toLocaleString()} sqft</strong>
                            </div>
                        )}
                        {property.constructionSqft && (
                            <div className="prop-stat">
                                <span className="prop-stat-label">Building</span>
                                <strong className="prop-stat-value">{Number(property.constructionSqft).toLocaleString()} sqft</strong>
                            </div>
                        )}
                        {property.rooms && (
                            <div className="prop-stat">
                                <span className="prop-stat-label">Rooms</span>
                                <strong className="prop-stat-value">{property.rooms}</strong>
                            </div>
                        )}
                        {property.bathrooms && (
                            <div className="prop-stat">
                                <span className="prop-stat-label">Bathrooms</span>
                                <strong className="prop-stat-value">{property.bathrooms}</strong>
                            </div>
                        )}
                        {property.stories > 0 && (
                            <div className="prop-stat">
                                <span className="prop-stat-label">Stories</span>
                                <strong className="prop-stat-value">{property.stories}</strong>
                            </div>
                        )}
                        <div className="prop-stat">
                            <span className="prop-stat-label">Neighborhood</span>
                            <strong className="prop-stat-value">{property.neighborhood}</strong>
                        </div>
                        <div className="prop-stat">
                            <span className="prop-stat-label">County</span>
                            <strong className="prop-stat-value">{property.county}</strong>
                        </div>
                    </div>

                    <a
                        href={`https://www.google.com/maps/search/?api=1&query=${property.lat},${property.lng}`}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-primary prop-modal-cta"
                    >
                        View on Google Maps →
                    </a>
                </div>
            </div>
        </div>
    );
};

/* ── Main Component ─────────────────────────────────────────── */
const Properties = () => {
    const mapRef = useRef(null);
    const [selected, setSelected] = useState(null);
    const t = useT({ en: propEn, es: propEs });

    const initMap = useCallback(() => {
        if (!mapRef.current || !window.google?.maps) return;

        const map = new window.google.maps.Map(mapRef.current, {
            center: VENUS_CENTER,
            zoom: 13,
            mapId: MAP_ID || null,
            disableDefaultUI: false,
            zoomControl: true,
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: true,
        });

        properties.forEach(prop => {
            const pin = new window.google.maps.marker.PinElement({
                background: prop.type === 'sale' ? '#025186' : '#fab80f',
                borderColor: prop.type === 'sale' ? '#013a60' : '#c99400',
                glyphColor: '#ffffff',
                scale: 1.3,
            });

            const marker = new window.google.maps.marker.AdvancedMarkerElement({
                map,
                position: { lat: prop.lat, lng: prop.lng },
                title: prop.description,
                content: pin.element,
            });

            marker.addListener('click', () => setSelected(prop));
        });

    }, []);

    useEffect(() => {
        if (!MAPS_KEY) return;
        if (window.google?.maps?.marker) { initMap(); return; }

        window.__initVenusMap = initMap;
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_KEY}&callback=__initVenusMap&libraries=marker&v=beta`;
        script.async = true;
        document.head.appendChild(script);

        return () => { delete window.__initVenusMap; };
    }, [initMap]);

    return (
        <section className="section section-alt" id="space">
            <div className="container">
                <div className="section-header text-center">
                    <h2 className="section-title">{t.title}<span>{t.titleSpan}</span></h2>
                    <p className="section-subtitle">{t.subtitle}</p>
                </div>

                {/* Legend */}
                <div className="prop-legend">
                    <span className="prop-legend-item">
                        <span className="prop-legend-dot prop-legend-dot--sale" />
                        {t.forSale}
                    </span>
                    <span className="prop-legend-item">
                        <span className="prop-legend-dot prop-legend-dot--lease" />
                        {t.forLease}
                    </span>
                    <span className="prop-legend-count">{properties.length} {t.listings}</span>
                </div>

                {/* Map */}
                <div id="property-search" className="prop-map-wrap">
                    {!MAPS_KEY && (
                        <div className="prop-map-placeholder">
                            <span className="prop-map-icon">🗺</span>
                            <p>Map configuration pending.</p>
                            <small>Add MAPS_KEY to maps-config.json</small>
                        </div>
                    )}
                    <div ref={mapRef} className="prop-map" />
                </div>

                {/* Property list below map */}
                <div className="prop-list">
                    {properties.map(prop => (
                        <div
                            key={prop.id}
                            className="prop-list-card"
                            onClick={() => setSelected(prop)}
                        >
                            <div className="prop-list-id">#{prop.id}</div>
                            <div className="prop-list-info">
                                <span className={`prop-badge prop-badge--${prop.type}`}>
                                    {prop.type === 'sale' ? t.forSale : t.forLease}
                                </span>
                                <h4>{prop.description}</h4>
                                <p>{prop.address} · {prop.neighborhood}</p>
                            </div>
                            <div className="prop-list-right">
                                {prop.saleCost && <strong className="prop-list-price">{prop.saleCost}</strong>}
                                {prop.leaseCost && <strong className="prop-list-price">{prop.leaseCost}</strong>}
                                {prop.acreage && <span className="prop-list-acreage">{prop.acreage} ac</span>}
                                <span className="prop-list-cta">{t.viewDetails} →</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {selected && (
                <PropertyModal property={selected} onClose={() => setSelected(null)} />
            )}
        </section>
    );
};

export default Properties;
